import React, { useCallback } from 'react';

import { Box } from '@provider-portal/design-common/kalep/components/Box/Box';
import { MenuItem } from '@provider-portal/design-common/kalep/components/Menu/MenuItem';
import { Radio } from '@provider-portal/design-common/kalep/components/Radio/Radio';
import { Typography } from '@provider-portal/design-common/kalep/components/Typography/Typography';
import { Skeleton } from '@provider-portal/design-common/kalep/components/Skeleton/Skeleton';
import { useIntl } from 'react-intl';

interface StoreListItemData {
  storeName: string;
  storeId: number;
}

export enum Availability {
  ONLINE = 'online',
  OFFLINE = 'offline',
  BUSY = 'busy',
}

const AVAILABILITY_INTL_LABEL: Record<Availability, string> = {
  online: 'mp2Components.storeSelector.availability.online',
  offline: 'mp2Components.storeSelector.availability.offline',
  busy: 'mp2Components.storeSelector.availability.busy',
};

const styles = {
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '0px',
    '&:hover': {
      backgroundColor: 'transparent!important',
    },
  },

  menuItemContent: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },

  storeName: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },

  statusBox: { paddingTop: '4px', paddingLeft: '36px' },

  statusSkeleton: { marginBottom: '4px' },
};

const AVAILABILITY_BADGE_STYLE: Record<Availability, { backgroundColor: string; color: string }> = {
  online: {
    backgroundColor: 'var(--color-bg-positive-secondary)',
    color: 'var(--color-content-positive-primary)',
  },
  offline: {
    backgroundColor: 'var(--color-bg-neutral-secondary)',
    color: 'var(--color-content-neutral-primary)',
  },
  busy: {
    backgroundColor: 'var(--color-bg-warning-secondary)',
    color: 'var(--color-content-warning-primary)',
  },
};

interface StoreListItemProps extends StoreListItemData {
  availability?: Availability;
  availabilityLoading: boolean;
  checked: boolean;
  onSelect: (storeId: number) => void;
}
const StoreListItem: React.FC<StoreListItemProps> = ({
  storeName,
  storeId,
  availability,
  availabilityLoading,
  checked,
  onSelect,
}) => {
  const intl = useIntl();

  const handleSelect = useCallback(() => {
    onSelect(storeId);
  }, [onSelect, storeId]);

  const availabilityBox = () => {
    if (availability) {
      return (
        <Typography
          variant="body-xs-regular"
          component="div"
          borderRadius="20px"
          style={{
            width: 'fit-content',
            padding: '4px 8px',
            ...AVAILABILITY_BADGE_STYLE[availability],
          }}
        >
          {intl.formatMessage({ id: AVAILABILITY_INTL_LABEL[availability] })}
        </Typography>
      );
    }

    if (availabilityLoading) {
      return <Skeleton variant="rounded" width="42px" height="20px" sx={styles.statusSkeleton} />;
    }

    return null;
  };

  return (
    <MenuItem sx={styles.menuItem} onClick={handleSelect}>
      <Box sx={styles.menuItemContent}>
        <Radio color="primary" value={storeId} onClick={handleSelect} checked={checked} />
        <Typography variant="body-m-regular" sx={styles.storeName}>
          {storeName}
        </Typography>
      </Box>
      <Box sx={styles.statusBox}>{availabilityBox()}</Box>
    </MenuItem>
  );
};

export interface StoreListGroup {
  parentName: string;
  stores: StoreListItemData[];
}

export interface StoreSelectorListProps {
  stores: StoreListGroup[];
  storeAvailabilities: Map<number, Availability>;
  storeAvailabilitiesLoading: boolean;
  selectedStoreId?: number;
  onSelectStore?: (storeId: number) => void;
}

export function StoreSelectorList({
  stores,
  storeAvailabilities,
  storeAvailabilitiesLoading,
  selectedStoreId,
  onSelectStore,
}: StoreSelectorListProps) {
  const handleSelectStore = useCallback(
    (storeId: number) => {
      if (onSelectStore) {
        onSelectStore(storeId);
      }
    },
    [onSelectStore],
  );

  const renderStoreList = React.useMemo(() => {
    return stores.map((storeGroup) => (
      <Box key={storeGroup.parentName}>
        <Box pt="12px" pb="8px">
          <Typography variant="body-l-accent">{storeGroup.parentName}</Typography>
        </Box>

        {storeGroup.stores.map((store) => (
          <StoreListItem
            key={store.storeId}
            storeName={store.storeName}
            availability={storeAvailabilities.get(store.storeId)}
            availabilityLoading={storeAvailabilitiesLoading}
            storeId={store.storeId}
            onSelect={handleSelectStore}
            checked={store.storeId === selectedStoreId}
          />
        ))}
      </Box>
    ));
  }, [handleSelectStore, selectedStoreId, storeAvailabilities, storeAvailabilitiesLoading, stores]);
  return <Box>{renderStoreList}</Box>;
}
